import { memo, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import {
	MainNavContainer,
	AppHeaderContainer,
	HeaderLogoContainer,
	HideHeaderButton,
	HideHeaderButtonWrapper,
} from "./header_styled_components";
import HeaderMenuItem from "./header_menu_item";
import HeaderMoreMenu from "./header_more_menu";
import { OnBibleLogo } from "../../assets/icons";
import { APPLICATION_ROUTES } from "../../utils/routes";
import HideArrowIcon from "../../assets/icons/header_icons/hide_arrow_icon";
import { HeaderUserIcon } from "../../assets/icons/header_icons";
import { useAppContext } from "../../../app_context";
import type { MenuItem } from "./nav_item.types";

// Log in and Sign up are one section: the item stays lit on both and names the one open.
const LOGIN_ITEM: MenuItem = {
	key: "login",
	path: APPLICATION_ROUTES.LOGIN,
	icon: HeaderUserIcon,
	textKey: "app-header.auth.login",
	activePaths: [APPLICATION_ROUTES.LOGIN, APPLICATION_ROUTES.REGISTRATION],
};
const REGISTRATION_ITEM: MenuItem = {
	...LOGIN_ITEM,
	path: APPLICATION_ROUTES.REGISTRATION,
	textKey: "app-header.auth.registration",
};

interface Props {
	navItems: MenuItem[];
	unreadMessages: number;
	unreadAnswersCount: number;
	showAdminButton: boolean;
	isNavItemDisabled: (menuItem: Pick<MenuItem, "path">) => boolean;
	onOpenSettings: (view?: string) => void;
	onOpenSupport: () => void;
	isSettingsModalOpen: boolean;
	// Set for a signed-out visitor: the top bar replaces the desktop sidebar entirely,
	// and the bar gains a Log in item of its own.
	forceTopHeader?: boolean;
}

// The tablet bar and, below 500px, the phone's bottom bar. Same markup either way —
// header_menu_item switches the items to icon-over-label at that width.
const TopHeader = ({
	navItems,
	unreadMessages,
	unreadAnswersCount,
	showAdminButton,
	isNavItemDisabled,
	onOpenSettings,
	onOpenSupport,
	isSettingsModalOpen,
	forceTopHeader = false,
}: Props) => {
	const { isAppMenuExpanded, setIsAppMenuExpanded, isHeaderMoreMenuOpen } = useAppContext();
	const { pathname } = useLocation();
	const authItem = pathname.startsWith(APPLICATION_ROUTES.REGISTRATION)
		? REGISTRATION_ITEM
		: LOGIN_ITEM;

	const toggleAppMenu = useCallback(
		(event: React.MouseEvent) => {
			event.stopPropagation();
			setIsAppMenuExpanded(prevState => !prevState);
		},
		[setIsAppMenuExpanded],
	);

	return (
		<div style={{ position: "relative" }} data-testid="header-root">
			{isAppMenuExpanded && (
				<AppHeaderContainer>
					<HeaderLogoContainer data-testid="logo-link">
						<Link to={APPLICATION_ROUTES.HOME}>
							<OnBibleLogo />
						</Link>
					</HeaderLogoContainer>

					<MainNavContainer data-testid="header-nav">
						{navItems.map(menuItem => (
							<HeaderMenuItem
								key={menuItem.key}
								menuItem={menuItem}
								isHeaderMoreMenuOpen={isHeaderMoreMenuOpen}
								unread={menuItem.key === "chats" ? unreadMessages : null}
								disabled={isNavItemDisabled(menuItem)}
							/>
						))}
						{forceTopHeader && (
							<HeaderMenuItem
								menuItem={authItem}
								isHeaderMoreMenuOpen={isHeaderMoreMenuOpen}
								disabled={isNavItemDisabled(authItem)}
							/>
						)}
					</MainNavContainer>

					<HeaderMoreMenu
						unreadAnswersCount={unreadAnswersCount}
						showAdminButton={showAdminButton}
						onOpenSettings={onOpenSettings}
						onOpenSupport={onOpenSupport}
						isSettingsModalOpen={isSettingsModalOpen}
						forceTopHeader={forceTopHeader}
					/>
				</AppHeaderContainer>
			)}

			<HideHeaderButtonWrapper isAppMenuExpanded={isAppMenuExpanded} onClick={toggleAppMenu}>
				<HideHeaderButton isAppMenuExpanded={isAppMenuExpanded} data-testid="hide-header-button">
					{/* The arrow flips through CSS, off the wrapper's data-collapsed. */}
					<HideArrowIcon />
				</HideHeaderButton>
			</HideHeaderButtonWrapper>
		</div>
	);
};

export default memo(TopHeader);
